// src/lib/footprint-editor/geometry.js
//
// The FootprintEditor's math, with no map: features in, features or
// geometries out. Every function works in the authoring frame (frame.js), in
// meters, and converts feet at units.js's 0.3048.
//
// ── THE CONVENTION (design §7.3, 149/151) ───────────────────────────────────
//
// A footprint is drawn in local feet with its anchor at the origin; length
// runs along +Y (grid north before rotation), width along X. 151 derives the
// stored geometry as
//
//   ST_Transform(ST_Translate(ST_Rotate(ST_Scale(fp, sx, sy), -radians(rot)),
//                             anchor_E, anchor_N), 4326)
//
// with sx = 0.3048 * width_ft / fp_width (1 when width_ft is NULL) and the
// same for y. ST_Rotate by a negative angle turns clockwise, so a point (x, y)
// of the shape lands at
//
//   E = x cos(rot) + y sin(rot),   N = -x sin(rot) + y cos(rot)
//
// from the anchor. toGrid() is that; fromGrid() is its inverse. A freeform
// footprint (no template) is ST_MakeEnvelope(-W/2, -L/2, W/2, L/2), so its
// scale is 1. A zone is ST_Buffer of the projected anchor with 16 segments a
// quadrant; a line's geometry is its line_geom.
//
// The client's geometry is a PREVIEW. The server's comes back on every save
// and replaces it (FootprintEditor's serverGeom); the fixture test holds this
// file to 151's transform within 1 cm.
import { M_PER_FT, ftToM, mToFt } from "../units.js";
import { toAxis, fromAxis } from "./padAxis.js";

export const LIMITS = Object.freeze({
  lengthFt: [0.5, 1000], widthFt: [0.5, 1000], radiusFt: [1, 2640], linePoints: [2, 500],
});
const rad = Math.PI / 180;
const clamp = (v, [lo, hi]) => Math.min(hi, Math.max(lo, v));
export const normalizeDeg = (d) => {
  const r = ((d % 360) + 360) % 360;
  return r >= 360 - 1e-9 ? 0 : r;
};

/** Shape-frame offset (meters) -> grid offset (meters), rotation clockwise. */
export function toGrid([x, y], rotDeg) {
  const c = Math.cos(rotDeg * rad), s = Math.sin(rotDeg * rad);
  return [x * c + y * s, -x * s + y * c];
}
/** Grid offset (meters) -> shape-frame offset (meters). */
export function fromGrid([e, n], rotDeg) {
  const c = Math.cos(rotDeg * rad), s = Math.sin(rotDeg * rad);
  return [e * c - n * s, e * s + n * c];
}

const anchorOf = (f) => f.anchor?.coordinates ?? null;
const point = (lngLat) => ({ type: "Point", coordinates: [lngLat[0], lngLat[1]] });

// ── Footprints ──────────────────────────────────────────────────────────────

function ringsBox(rings) {
  let xmin = Infinity, xmax = -Infinity, ymin = Infinity, ymax = -Infinity;
  for (const r of rings) for (const [x, y] of r) {
    if (x < xmin) xmin = x; if (x > xmax) xmax = x;
    if (y < ymin) ymin = y; if (y > ymax) ymax = y;
  }
  return { xmin, xmax, ymin, ymax };
}

/**
 * The footprint before scaling, in feet, and the scale 151 applies to it.
 * A template's footprint_ft is the template's; a freeform footprint is the
 * envelope of its own length and width.
 */
export function footprintBase(feature, template) {
  const fp = template?.footprint_ft;
  if (fp && fp.type === "Polygon") {
    const box = ringsBox(fp.coordinates);
    const dx = box.xmax - box.xmin, dy = box.ymax - box.ymin;
    return {
      rings: fp.coordinates, box,
      sx: feature.width_ft != null && dx ? feature.width_ft / dx : 1,
      sy: feature.length_ft != null && dy ? feature.length_ft / dy : 1,
    };
  }
  const W = feature.width_ft, L = feature.length_ft;
  if (!(W > 0) || !(L > 0)) return null;
  const box = { xmin: -W / 2, xmax: W / 2, ymin: -L / 2, ymax: L / 2 };
  return { rings: [[[box.xmin, box.ymin], [box.xmin, box.ymax], [box.xmax, box.ymax], [box.xmax, box.ymin], [box.xmin, box.ymin]]], box, sx: 1, sy: 1 };
}

/** The scaled extent in shape-frame feet: what the handles sit on. */
export function footprintExtent(feature, template) {
  const b = footprintBase(feature, template);
  if (!b) return null;
  return { xmin: b.box.xmin * b.sx, xmax: b.box.xmax * b.sx, ymin: b.box.ymin * b.sy, ymax: b.box.ymax * b.sy };
}

/** Effective length and width in feet, the template's when not overridden. */
export function footprintDims(feature, template) {
  const e = footprintExtent(feature, template);
  return e ? { lengthFt: e.ymax - e.ymin, widthFt: e.xmax - e.xmin } : null;
}

export const zoneRadiusFt = (feature, template) => feature.radius_ft ?? template?.radius_ft ?? null;

/** Shape-frame feet -> [E, N] meters in the frame. */
function placeLocal(frame, feature, [xFt, yFt]) {
  const [ax, ay] = frame.toLocal(anchorOf(feature));
  const [de, dn] = toGrid([ftToM(xFt), ftToM(yFt)], feature.rotation_deg ?? 0);
  return [ax + de, ay + dn];
}

/** The outline in the frame, [E, N] meters per vertex: rings for footprints and zones, one path for lines. */
export function localRings(feature, template, frame) {
  if (feature.kind === "footprint") {
    const b = footprintBase(feature, template);
    if (!b || !anchorOf(feature)) return null;
    return b.rings.map((r) => r.map(([x, y]) => placeLocal(frame, feature, [x * b.sx, y * b.sy])));
  }
  if (feature.kind === "zone") {
    const r = zoneRadiusFt(feature, template);
    if (!(r > 0) || !anchorOf(feature)) return null;
    const [ax, ay] = frame.toLocal(anchorOf(feature));
    const rm = ftToM(r), n = 64;        // quad_segs=16
    const ring = [];
    for (let i = 0; i <= n; i++) {
      const t = (i % n) * (2 * Math.PI / n);
      ring.push([ax + rm * Math.cos(t), ay - rm * Math.sin(t)]);
    }
    return [ring];
  }
  if (feature.kind === "line") {
    const c = feature.line_geom?.coordinates;
    return c?.length ? [c.map((p) => frame.toLocal(p))] : null;
  }
  return null;
}

/** The client's preview of the geometry 151 will store, in 4326. */
export function previewGeometry(feature, template, frame) {
  const rings = localRings(feature, template, frame);
  if (!rings) return null;
  if (feature.kind === "line") return feature.line_geom;
  return { type: "Polygon", coordinates: rings.map((r) => r.map((p) => frame.toLngLat(p))) };
}

// The point halfway along a line, and i, the end of the segment it is on.
function halfway(c) {
  const seg = [];
  let total = 0;
  for (let i = 1; i < c.length; i += 1) { const d = Math.hypot(c[i][0] - c[i - 1][0], c[i][1] - c[i - 1][1]); seg.push(d); total += d; }
  let left = total / 2;
  for (let i = 1; i < c.length; i += 1) {
    if (left <= seg[i - 1] && seg[i - 1] > 0) {
      const t = left / seg[i - 1];
      return { at: [c[i - 1][0] + (c[i][0] - c[i - 1][0]) * t, c[i - 1][1] + (c[i][1] - c[i - 1][1]) * t], i };
    }
    left -= seg[i - 1];
  }
  return { at: c[0], i: 1 };
}

/** The segment a line's label sits on (labelPoint's): its two ends, or null. */
export function labelSegment(geometry) {
  const c = geometry?.type === "LineString" ? geometry.coordinates : null;
  if (!(c?.length >= 2)) return null;
  const { i } = halfway(c);
  return [c[i - 1], c[i]];
}

/**
 * Where a feature's label sits, in 4326: a polygon's area centroid (its outer
 * ring; the vertex mean if it has no area), or the point halfway along a line.
 * Planar in degrees, which is exact enough across a pad.
 */
export function labelPoint(geometry) {
  if (!geometry) return null;
  if (geometry.type === "LineString") {
    const c = geometry.coordinates;
    return c?.length ? halfway(c).at : null;
  }
  const ring = geometry.type === "Polygon" ? geometry.coordinates?.[0]
    : geometry.type === "MultiPolygon" ? geometry.coordinates?.[0]?.[0] : null;
  if (!ring?.length) return null;
  const pts = ring.length > 1 && ring[0][0] === ring.at(-1)[0] && ring[0][1] === ring.at(-1)[1] ? ring.slice(0, -1) : ring;
  // Relative to the first vertex: on raw degrees (~102, ~32) the shoelace's
  // cross terms cancel catastrophically for a shape a few meters across.
  const [ox, oy] = pts[0];
  let a = 0, cx = 0, cy = 0;
  for (let i = 0; i < pts.length; i += 1) {
    const [x0, y0] = [pts[i][0] - ox, pts[i][1] - oy], [x1, y1] = [pts[(i + 1) % pts.length][0] - ox, pts[(i + 1) % pts.length][1] - oy];
    const k = x0 * y1 - x1 * y0;
    a += k; cx += (x0 + x1) * k; cy += (y0 + y1) * k;
  }
  if (Math.abs(a) < 1e-18) return [pts.reduce((t, p) => t + p[0], 0) / pts.length, pts.reduce((t, p) => t + p[1], 0) / pts.length];
  return [ox + cx / (3 * a), oy + cy / (3 * a)];
}

/** The axis-aligned box of a feature in the frame (grid E/N), meters. */
export function localBounds(feature, template, frame) {
  const rings = localRings(feature, template, frame);
  if (!rings) return null;
  let xmin = Infinity, xmax = -Infinity, ymin = Infinity, ymax = -Infinity;
  for (const r of rings) for (const [x, y] of r) {
    if (x < xmin) xmin = x; if (x > xmax) xmax = x;
    if (y < ymin) ymin = y; if (y > ymax) ymax = y;
  }
  return { xmin, xmax, ymin, ymax };
}

// ── Handles ─────────────────────────────────────────────────────────────────

export const RESIZE_HANDLES = Object.freeze(["n", "ne", "e", "se", "s", "sw", "w", "nw"]);

/** The rotate handle's distance beyond the north edge, in feet. */
export const rotateHandleOffsetFt = (lengthFt) => Math.max(8, 0.2 * lengthFt);

/**
 * Handles for one selected feature: resize handles on the shape's own edges
 * and corners, aligned to it in the frame, and a rotate handle beyond its
 * north edge; a zone has one radius handle. A line has a vertex handle on
 * each vertex ("v:<i>") and an insert handle on each segment's midpoint
 * ("m:<i>", between vertex i and i + 1).
 * @returns {{handle: string, lngLat: number[], en: number[]}[]}
 */
export function handlesFor(feature, template, frame) {
  const out = [];
  const add = (handle, en) => out.push({ handle, en, lngLat: frame.toLngLat(en) });
  if (feature.kind === "footprint") {
    const e = footprintExtent(feature, template);
    if (!e || !anchorOf(feature)) return out;
    const cx = (e.xmin + e.xmax) / 2, cy = (e.ymin + e.ymax) / 2;
    const at = {
      n: [cx, e.ymax], ne: [e.xmax, e.ymax], e: [e.xmax, cy], se: [e.xmax, e.ymin],
      s: [cx, e.ymin], sw: [e.xmin, e.ymin], w: [e.xmin, cy], nw: [e.xmin, e.ymax],
    };
    for (const h of RESIZE_HANDLES) add(h, placeLocal(frame, feature, at[h]));
    add("rotate", placeLocal(frame, feature, [cx, e.ymax + rotateHandleOffsetFt(e.ymax - e.ymin)]));
  } else if (feature.kind === "zone") {
    const r = zoneRadiusFt(feature, template);
    if (!(r > 0) || !anchorOf(feature)) return out;
    const [ax, ay] = frame.toLocal(anchorOf(feature));
    add("radius", [ax + ftToM(r), ay]);
  } else if (feature.kind === "line") {
    const c = (feature.line_geom?.coordinates ?? []).map((p) => frame.toLocal(p));
    c.forEach((en, i) => add(`v:${i}`, en));
    for (let i = 1; i < c.length; i++) add(`m:${i - 1}`, [(c[i - 1][0] + c[i][0]) / 2, (c[i - 1][1] + c[i][1]) / 2]);
  }
  return out;
}

/** A line handle's parts: {type: "vertex" | "mid", index}, or null. */
export function lineHandle(handle) {
  const m = /^([vm]):(\d+)$/.exec(handle ?? "");
  return m ? { type: m[1] === "v" ? "vertex" : "mid", index: Number(m[2]) } : null;
}

// ── Line vertices ───────────────────────────────────────────────────────────
//
// Pure: a line in, a line out (or a refusal). The editor makes each one an
// operation; positions are lng/lat, already snapped by the caller.

const withCoords = (feature, coords) => ({ ...feature, line_geom: { type: "LineString", coordinates: coords } });
const coordsOf = (feature) => feature.line_geom?.coordinates ?? [];

/** Move vertex i to lngLat. */
export function moveVertex(feature, i, lngLat) {
  const c = coordsOf(feature);
  if (feature.kind !== "line" || !(i >= 0 && i < c.length)) return null;
  return withCoords(feature, c.map((p, k) => (k === i ? [lngLat[0], lngLat[1]] : p)));
}

/** Insert a vertex at lngLat after vertex i (on segment i); at most 500. */
export function insertVertex(feature, i, lngLat) {
  const c = coordsOf(feature);
  if (feature.kind !== "line" || !(i >= 0 && i < c.length - 1) || c.length >= LIMITS.linePoints[1]) return null;
  return withCoords(feature, [...c.slice(0, i + 1), [lngLat[0], lngLat[1]], ...c.slice(i + 1)]);
}

/**
 * admin-api/lib/padplanner/features.js LINE_REPEAT_TOL_M, the server's rule:
 * a point within 1 cm of the last point kept, in the frame, is dropped on
 * every save. docs/architecture/padplanner/fixtures/pad_plan_line_repeats.json
 * (PostGIS's distances) holds both to the same answers.
 */
export const LINE_REPEAT_TOL_M = 0.01;

/**
 * The positions without a repeat (a zero-length segment): with a frame, none
 * within LINE_REPEAT_TOL_M of the last kept, as the server stores them;
 * without one, no exact repeat. The same array when none is dropped, or when
 * fewer than two would remain (the server refuses that line, not this).
 */
export function dropRepeatedPoints(coords, frame = null) {
  const c = coords ?? [];
  const near = frame
    ? (p, q) => { const a = frame.toLocal(p), b = frame.toLocal(q); return Math.hypot(b[0] - a[0], b[1] - a[1]) <= LINE_REPEAT_TOL_M; }
    : (p, q) => p[0] === q[0] && p[1] === q[1];
  const out = [];
  for (const p of c) if (!out.length || !near(out[out.length - 1], p)) out.push(p);
  return out.length === c.length || out.length < LIMITS.linePoints[0] ? c : out;
}

/** A line without repeated positions (in `frame`, when given); the same object when there are none. */
export function withoutRepeats(feature, frame = null) {
  if (feature?.kind !== "line") return feature;
  const c = coordsOf(feature), d = dropRepeatedPoints(c, frame);
  return d === c ? feature : withCoords(feature, d);
}

/** Remove vertex i; a line keeps at least two. */
export function deleteVertex(feature, i) {
  const c = coordsOf(feature);
  if (feature.kind !== "line" || !(i >= 0 && i < c.length) || c.length <= LIMITS.linePoints[0]) return null;
  return withCoords(feature, c.filter((_, k) => k !== i));
}

/** A line's length in feet in the frame: per segment and in total. */
export function lineLengthsFt(coords, frame) {
  const en = (coords ?? []).map((p) => frame.toLocal(p));
  const segments = [];
  for (let i = 1; i < en.length; i++) segments.push(mToFt(Math.hypot(en[i][0] - en[i - 1][0], en[i][1] - en[i - 1][1])));
  return { segments, total: segments.reduce((t, v) => t + v, 0) };
}

/**
 * Where a line vertex lands (frame meters), and why. In order, the first
 * that applies wins: another line's endpoint within endpointTolM (a road
 * joins a road), a perimeter edge within tolM, the grid. @returns
 * {{en: number[], snapped: null|"endpoint"|"perimeter"|"grid"}}
 */
export function snapLinePoint(en, { grid = false, gridFt = 5, axis = null, edgesLocal = [], tolM = 0, endpoints = [], endpointTolM = 0 } = {}) {
  let best = null, bestD = endpointTolM;
  for (const p of endpoints) {
    const d = Math.hypot(p[0] - en[0], p[1] - en[1]);
    if (d <= bestD) { bestD = d; best = p; }
  }
  if (best) return { en: [best[0], best[1]], snapped: "endpoint" };
  if (edgesLocal.length && tolM > 0) {
    const c = perimeterSnapCorrection([en], edgesLocal, tolM);
    if (c) return { en: [en[0] + c[0], en[1] + c[1]], snapped: "perimeter" };
  }
  if (grid) return { en: snapPointToGrid(en, gridFt, axis), snapped: "grid" };
  return { en, snapped: null };
}

const snapTo = (v, step) => (step > 0 ? Math.round(v / step) * step : v);

/**
 * Resize a footprint by dragging one of its handles to cursorEN (frame
 * meters). The opposite edge (or corner) stays where it is: the anchor moves
 * by what the scale moved it. Dimensions snap to stepFt when given (the
 * editor's resize step, not the position grid) and are held to 151's
 * 0.5–1,000 ft.
 */
export function resizeFootprint(feature, template, frame, handle, cursorEN, { stepFt = null } = {}) {
  const b = footprintBase(feature, template);
  if (!b) return feature;
  const [ax, ay] = frame.toLocal(anchorOf(feature));
  const rot = feature.rotation_deg ?? 0;
  const [cxM, cyM] = fromGrid([cursorEN[0] - ax, cursorEN[1] - ay], rot);
  const x = mToFt(cxM), y = mToFt(cyM);
  const { xmin: a0, xmax: a1, ymin: b0, ymax: b1 } = b.box;
  let { sx, sy } = b;
  let shiftX = 0, shiftY = 0;
  const next = { ...feature };
  const dim = (v, lim) => clamp(stepFt ? Math.max(stepFt, snapTo(v, stepFt)) : v, lim);

  if (handle.includes("n") || handle.includes("s")) {
    const base = b1 - b0;
    const L = handle.includes("n") ? dim(y - b0 * sy, LIMITS.lengthFt) : dim(b1 * sy - y, LIMITS.lengthFt);
    const s2 = L / base;
    shiftY = handle.includes("n") ? b0 * sy - b0 * s2 : b1 * sy - b1 * s2;
    sy = s2; next.length_ft = L;
  }
  if (handle.includes("e") || handle.includes("w")) {
    const base = a1 - a0;
    const W = handle.includes("e") ? dim(x - a0 * sx, LIMITS.widthFt) : dim(a1 * sx - x, LIMITS.widthFt);
    const s2 = W / base;
    shiftX = handle.includes("e") ? a0 * sx - a0 * s2 : a1 * sx - a1 * s2;
    sx = s2; next.width_ft = W;
  }
  // A freeform footprint stays centered on its anchor: its envelope is
  // rebuilt from the new length and width, which is what the shift accounts for.
  const [de, dn] = toGrid([ftToM(shiftX), ftToM(shiftY)], rot);
  next.anchor = point(frame.toLngLat([ax + de, ay + dn]));
  return next;
}

/** A zone's radius, dragged to cursorEN, in stepFt increments when given. */
export function resizeZone(feature, frame, cursorEN, { stepFt = null } = {}) {
  const [ax, ay] = frame.toLocal(anchorOf(feature));
  const r = mToFt(Math.hypot(cursorEN[0] - ax, cursorEN[1] - ay));
  return { ...feature, radius_ft: clamp(stepFt ? Math.max(stepFt, snapTo(r, stepFt)) : r, LIMITS.radiusFt) };
}

/** Rotation from the anchor to the cursor, clockwise from grid north; snapped to stepDeg when given. */
export function rotationToward(feature, frame, cursorEN, { stepDeg = null, offsetDeg = 0 } = {}) {
  const [ax, ay] = frame.toLocal(anchorOf(feature));
  const deg = Math.atan2(cursorEN[0] - ax, cursorEN[1] - ay) / rad;
  // offsetDeg: the pad axis (padAxis.js), so the steps are the pad's; the result stays absolute.
  return normalizeDeg(stepDeg ? snapTo(deg - offsetDeg, stepDeg) + offsetDeg : deg);
}

// ── Moving, snapping, aligning ──────────────────────────────────────────────

/** Translate a feature by [dE, dN] meters in the frame. */
export function translate(feature, frame, [dE, dN]) {
  if (feature.kind === "line") {
    const c = feature.line_geom.coordinates.map((p) => {
      const [x, y] = frame.toLocal(p);
      return frame.toLngLat([x + dE, y + dN]);
    });
    return { ...feature, line_geom: { type: "LineString", coordinates: c } };
  }
  const [x, y] = frame.toLocal(anchorOf(feature));
  return { ...feature, anchor: point(frame.toLngLat([x + dE, y + dN])) };
}

/** A feature's reference point in the frame: its anchor, or a line's first vertex. */
export function referenceEN(feature, frame) {
  const p = feature.kind === "line" ? feature.line_geom?.coordinates?.[0] : anchorOf(feature);
  return p ? frame.toLocal(p) : null;
}

/**
 * A point in the frame, on the grid: the absolute grid (E and N whole steps),
 * or with an axis (padAxis.js) the pad's, whole steps in pad meters.
 */
export function snapPointToGrid([e, n], gridFt, axis = null) {
  const g = ftToM(gridFt);
  if (!axis) return [snapTo(e, g), snapTo(n, g)];
  const [x, y] = toAxis([e, n], axis);
  return fromAxis([snapTo(x, g), snapTo(y, g)], axis);
}

function nearestOnSegment([px, py], [x0, y0], [x1, y1]) {
  const dx = x1 - x0, dy = y1 - y0;
  const len2 = dx * dx + dy * dy;
  const t = len2 ? Math.max(0, Math.min(1, ((px - x0) * dx + (py - y0) * dy) / len2)) : 0;
  return [x0 + t * dx, y0 + t * dy];
}

/** The perimeter's edges in the frame, as rings of [E, N]. */
export function perimeterLocal(perimeter, frame) {
  if (!perimeter) return [];
  const polys = perimeter.type === "Polygon" ? [perimeter.coordinates]
    : perimeter.type === "MultiPolygon" ? perimeter.coordinates : [];
  return polys.flatMap((poly) => poly.map((ring) => ring.map((p) => frame.toLocal(p))));
}

/**
 * The correction that puts the vertex nearest a perimeter edge on it, if
 * that vertex is within tolM. @returns {[number, number]|null}
 */
export function perimeterSnapCorrection(vertices, edgesLocal, tolM) {
  let best = null, bestD = tolM;
  for (const v of vertices) for (const ring of edgesLocal) {
    for (let i = 1; i < ring.length; i++) {
      const q = nearestOnSegment(v, ring[i - 1], ring[i]);
      const d = Math.hypot(q[0] - v[0], q[1] - v[1]);
      if (d < bestD) { bestD = d; best = [q[0] - v[0], q[1] - v[1]]; }
    }
  }
  return best;
}

/**
 * The delta a move actually applies. Grid first: the first feature's
 * reference point lands on the grid. Then the perimeter: a vertex within
 * tolM of a perimeter edge lands on it, and that wins over the grid.
 * @returns {{delta: [number, number], snapped: null|"grid"|"perimeter"}}
 */
export function snapMove({ features, templates, frame, delta, snapping, edgesLocal = [], tolM = 0 }) {
  let d = delta, snapped = null;
  const first = features[0];
  if (snapping?.grid && first) {
    const ref = referenceEN(first, frame);
    if (ref) {
      const target = snapPointToGrid([ref[0] + d[0], ref[1] + d[1]], snapping.gridFt, snapping.axis ?? null);
      d = [target[0] - ref[0], target[1] - ref[1]];
      snapped = "grid";
    }
  }
  if (snapping?.perimeter && edgesLocal.length && tolM > 0) {
    const vertices = [];
    for (const f of features) {
      const rings = localRings(translate(f, frame, d), templates?.get?.(f.template_id) ?? null, frame);
      if (f.kind !== "zone") for (const r of rings ?? []) vertices.push(...r);
    }
    const c = perimeterSnapCorrection(vertices, edgesLocal, tolM);
    if (c) { d = [d[0] + c[0], d[1] + c[1]]; snapped = "perimeter"; }
  }
  return { delta: d, snapped };
}

export const ALIGN_MODES = Object.freeze(["left", "right", "top", "bottom", "center", "middle"]);

/**
 * Align features in the frame: left/right/top/bottom to the selection's
 * extreme edge, center (vertical axis) and middle (horizontal axis) to the
 * selection's center. Grid east is right and grid north is top.
 * @returns {object[]} the moved features, in the order given
 */
export function align(features, templates, frame, mode) {
  if (!ALIGN_MODES.includes(mode)) throw new Error(`footprint-editor: align mode ${mode}`);
  const boxes = features.map((f) => localBounds(f, templates?.get?.(f.template_id) ?? null, frame));
  if (boxes.some((b) => !b)) return features;
  const all = boxes.reduce((u, b) => ({
    xmin: Math.min(u.xmin, b.xmin), xmax: Math.max(u.xmax, b.xmax), ymin: Math.min(u.ymin, b.ymin), ymax: Math.max(u.ymax, b.ymax),
  }));
  return features.map((f, i) => {
    const b = boxes[i];
    const d = {
      left: [all.xmin - b.xmin, 0], right: [all.xmax - b.xmax, 0],
      bottom: [0, all.ymin - b.ymin], top: [0, all.ymax - b.ymax],
      center: [(all.xmin + all.xmax) / 2 - (b.xmin + b.xmax) / 2, 0],
      middle: [0, (all.ymin + all.ymax) / 2 - (b.ymin + b.ymax) / 2],
    }[mode];
    return d[0] || d[1] ? translate(f, frame, d) : f;
  });
}

// ── Dimensions ──────────────────────────────────────────────────────────────

const fmtFt = (ft) => `${Math.abs(ft - Math.round(ft)) < 0.05 ? Math.round(ft).toLocaleString("en-US") : ft.toFixed(1)} ft`;

/**
 * Edge-length labels for a selected feature, at each edge's midpoint. A
 * footprint's carry `center` (the shape's middle) so the overlay can push
 * each label outward, off the edge: on a narrow shape, labels centered on
 * the midpoints sit on top of each other.
 */
export function dimensionLabels(feature, template, frame) {
  if (feature.kind === "footprint") {
    const b = footprintBase(feature, template);
    if (!b || !anchorOf(feature)) return [];
    const e = footprintExtent(feature, template);
    const center = frame.toLngLat(placeLocal(frame, feature, [(e.xmin + e.xmax) / 2, (e.ymin + e.ymax) / 2]));
    const out = [];
    for (const ring of b.rings) for (let i = 1; i < ring.length; i++) {
      const p0 = [ring[i - 1][0] * b.sx, ring[i - 1][1] * b.sy], p1 = [ring[i][0] * b.sx, ring[i][1] * b.sy];
      const len = Math.hypot(p1[0] - p0[0], p1[1] - p0[1]);
      if (len < 1e-6) continue;
      out.push({ text: fmtFt(len), lengthFt: len, center, lngLat: frame.toLngLat(placeLocal(frame, feature, [(p0[0] + p1[0]) / 2, (p0[1] + p1[1]) / 2])) });
    }
    return out;
  }
  if (feature.kind === "zone") {
    const r = zoneRadiusFt(feature, template);
    if (!(r > 0) || !anchorOf(feature)) return [];
    const [ax, ay] = frame.toLocal(anchorOf(feature));
    return [{ text: `r ${fmtFt(r)}`, lengthFt: r, lngLat: frame.toLngLat([ax + ftToM(r) / 2, ay]) }];
  }
  if (feature.kind === "line") return lineDimensionLabels(feature.line_geom?.coordinates ?? [], frame);
  return [];
}

/**
 * A line's labels: each segment's length at its midpoint, and the total
 * ("total 412 ft") at the last vertex. One segment gets only its length.
 */
export function lineDimensionLabels(coords, frame) {
  if (!coords || coords.length < 2) return [];
  const { segments, total } = lineLengthsFt(coords, frame);
  const out = segments.map((ft, i) => {
    const a = frame.toLocal(coords[i]), b = frame.toLocal(coords[i + 1]);
    // ends: the overlay sets the label beside the segment, off its midpoint handle.
    return { text: fmtFt(ft), lengthFt: ft, segment: i, ends: [coords[i], coords[i + 1]], lngLat: frame.toLngLat([(a[0] + b[0]) / 2, (a[1] + b[1]) / 2]) };
  });
  // Past the last vertex, along the last segment (center: the one before it).
  const n = coords.length;
  if (segments.length > 1) out.push({ text: `total ${fmtFt(total)}`, lengthFt: total, total: true, center: coords[n - 2], lngLat: coords[n - 1] });
  return out;
}

/** A length as the editor writes it: whole feet when within 0.05 ft, else one decimal. */
export const formatFeet = fmtFt;

/** Web Mercator meters per CSS pixel at a latitude, for MapLibre's 512 px tiles. */
export function metersPerPixel(lat, zoom) {
  return (40075016.686 * Math.cos(lat * rad)) / (512 * 2 ** zoom);
}

/**
 * A scale bar in feet that fits in maxPx: the largest 1, 2 or 5 x 10^k feet.
 * Always feet, never miles (MapLibre's imperial control switches at 5,280).
 */
export function scaleBarFeet(mPerPx, maxPx = 100) {
  const maxFt = (maxPx * mPerPx) / M_PER_FT;
  if (!(maxFt > 0)) return null;
  const p = 10 ** Math.floor(Math.log10(maxFt));
  const feet = [5, 2, 1].map((k) => k * p).find((v) => v <= maxFt) ?? p;
  return { feet, px: (feet * M_PER_FT) / mPerPx, label: `${feet.toLocaleString("en-US")} ft` };
}
